import { ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components/macro'

export type PositionSortKey = 'value' | 'fees' | 'created'
export type PositionSortDirection = 'asc' | 'desc'

export type PositionMetrics = {
  estimatedValue?: number
  feesUSD?: number
  createdAt?: number
  oneLocked?: number
}

const POSITION_TABLE_COLUMNS =
  'minmax(190px, 1.35fr) minmax(186px, 1.2fr) minmax(200px, 1.3fr) minmax(96px, 0.7fr) minmax(88px, 0.6fr) minmax(104px, 0.68fr)'

const PositionTableHeader = styled.div`
  display: grid;
  grid-template-columns: ${POSITION_TABLE_COLUMNS};
  min-width: 930px;
  padding: 12px 20px 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

const positionTableItem = css<{ $order?: number; $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
  order: ${({ $order }) => $order ?? 9999};
  min-width: 930px;
  margin: 4px 8px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid transparent;
  border-radius: 12px;
  transition: background-color 125ms ease, border-color 125ms ease;

  &:hover {
    background: ${({ theme }) => theme.hoverDefault};
    border-color: ${({ theme }) => theme.backgroundOutline};
  }
`

export const PositionTableRow = styled(Link)<{ $order?: number; $hidden?: boolean }>`
  ${positionTableItem}
  display: ${({ $hidden }) => ($hidden ? 'none' : 'grid')};
  grid-template-columns: ${POSITION_TABLE_COLUMNS};
  min-height: 76px;
  padding: 14px 12px;
  align-items: center;
  text-decoration: none;
`

export const ExpandablePositionTableCard = styled.div<{ $order?: number; $hidden?: boolean; $expanded?: boolean }>`
  ${positionTableItem}
  border-color: ${({ $expanded, theme }) => ($expanded ? theme.backgroundOutline : 'transparent')};
`

export const PositionTableSummaryButton = styled.button`
  display: grid;
  grid-template-columns: ${POSITION_TABLE_COLUMNS};
  align-items: center;
  width: 100%;
  min-height: 76px;
  padding: 14px 12px;
  color: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
  font: inherit;
  text-align: left;
`

export const PositionCell = styled.div`
  min-width: 0;
  padding: 0 8px;
`

export const PoolIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const CellPrimary = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const CellSecondary = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  line-height: 16px;
`

export const VersionBadge = styled.span`
  padding: 1px 5px;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.backgroundOutline};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: lowercase;
`

export const RangeValues = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`

const DistributionTrack = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 7px;
  overflow: visible;
  background: ${({ theme }) => theme.backgroundOutline};
  border-radius: 999px;
`

const DistributionSegment = styled.div<{ $color: string; $width: number; $side: 'left' | 'right' }>`
  width: ${({ $width }) => `${$width}%`};
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: ${({ $side }) => ($side === 'left' ? '999px 0 0 999px' : '0 999px 999px 0')};
`

const DistributionMarker = styled.div<{ $offset: number }>`
  position: absolute;
  top: -7px;
  left: ${({ $offset }) => `${$offset}%`};
  width: 0;
  height: 0;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  border-top: 6px solid ${({ theme }) => theme.textPrimary};
  transform: translateX(-50%);
`

const DistributionLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin-top: 7px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 10px;
  white-space: nowrap;
`

export function TokenDistribution({
  firstPercent,
  firstColor,
  secondColor,
  firstSymbol,
  secondSymbol,
}: {
  firstPercent: number
  firstColor: string
  secondColor: string
  firstSymbol?: string
  secondSymbol?: string
}) {
  const safeFirstPercent = Math.max(0, Math.min(100, firstPercent))
  const secondPercent = 100 - safeFirstPercent

  return (
    <>
      <DistributionTrack>
        <DistributionSegment $side="left" $color={firstColor} $width={safeFirstPercent} />
        <DistributionSegment $side="right" $color={secondColor} $width={secondPercent} />
        <DistributionMarker $offset={safeFirstPercent} />
      </DistributionTrack>
      <DistributionLabels>
        <span>
          {safeFirstPercent.toFixed(0)}% {firstSymbol}
        </span>
        <span>
          {secondPercent.toFixed(0)}% {secondSymbol}
        </span>
      </DistributionLabels>
    </>
  )
}

const HeaderLabel = styled.span`
  padding: 7px 8px;
`

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  width: fit-content;
  padding: 7px 8px;
  color: inherit;
  background: transparent;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
  font: inherit;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.hoverDefault};
  }
`

export function SortablePositionHeader({
  sortKey,
  sortDirection,
  onSort,
}: {
  sortKey: PositionSortKey
  sortDirection: PositionSortDirection
  onSort: (key: PositionSortKey) => void
}) {
  const sortableHeader = (label: ReactNode, key: PositionSortKey) => (
    <SortButton type="button" onClick={() => onSort(key)} aria-label={`Sort positions by ${key}`}>
      {label}
      {sortKey === key && (sortDirection === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
    </SortButton>
  )

  return (
    <PositionTableHeader>
      <HeaderLabel>Pool</HeaderLabel>
      <HeaderLabel>Position</HeaderLabel>
      <HeaderLabel>Distribution</HeaderLabel>
      {sortableHeader('Value', 'value')}
      {sortableHeader('Fees', 'fees')}
      {sortableHeader('Created', 'created')}
    </PositionTableHeader>
  )
}

export function formatPositionUSD(value?: number) {
  return (
    value?.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) ?? '—'
  )
}
